/**
 * engine/demanda.ts — predicados-folha da tabela de demanda por unidade (EL-154) e os
 * derivados que alimentam EL-083 / EL-056 / EL-057 / EL-114.
 *
 * Todas as funções são puras: só LEEM `lev`/`u`/`v` e devolvem valor novo. Nenhuma muta
 * os argumentos (no protótipo a mutação estava nos acessores `demDet()`/`vagasKeyArr()`,
 * que NÃO migram — o repositório entrega os arrays já normalizados).
 *
 * Origem: fincast-viabilidade-v67.html, linhas 1416-1419, 2862-2878 e 2975-2996.
 */

import type {
  BaseCalculo, ContagemDemanda, DerivadosDemanda, IsoDate, Lev, TravasDemanda, Unidade, Vaga,
} from './tipos';

/**
 * v67 linha 2877 — vaga permutada E informada à Caixa sai de TUDO (VGV / FSF / terreno).
 */
export function _vagaExcl(v: Vaga | null | undefined): boolean {
  return !!(v && v.permutada && v.permInfCaixa);
}

/**
 * v67 linha 2878 — unidade permutada E informada ao banco.
 *
 * COMPORTAMENTO PRESERVADO DO PROTÓTIPO: esta função só é usada em `demDerivar` (VGV).
 * NÃO filtra `fsfPorUnidade` nem `terrenoGarFrac` — lá o gate é `_ingressaValida`, que já
 * exclui `u.permutada`. Essa assimetria é intencional na extração.
 */
export function _unitExcl(u: Unidade | null | undefined): boolean {
  return !!(u && u.permutada && u.permInfBanco);
}

/**
 * v67 linha 2864 — vagas de `lev.vagasDem` vinculadas à unidade e não excluídas.
 * Usa SEMPRE `vagasDem` (o EL-154 é o driver), mesmo quando a unidade veio do lado `det`.
 */
export function _vagasVinc(lev: Lev, u: Unidade): Vaga[] {
  const vg = (lev && Array.isArray(lev.vagasDem)) ? lev.vagasDem : [];
  const ids = Array.isArray(u.vagas) ? u.vagas : [];
  return vg.filter(v => ids.includes(v.id) && !_vagaExcl(v));
}

/** v67 linha 2865 — avaliação efetiva = unidade + vagas vinculadas não excluídas. */
export function _avalEfet(lev: Lev, u: Unidade): number {
  return (u.avaliacao || 0) + _vagasVinc(lev, u).reduce((s, v) => s + (v.avaliacao || 0), 0);
}

/**
 * v67 linha 2866 — fração ideal efetiva = unidade + vagas vinculadas não excluídas.
 * ⚠ `u.m2` carrega FRAÇÃO IDEAL (0..1) no lado `dem` desde a v54, não metro quadrado.
 */
export function _fracEfet(lev: Lev, u: Unidade): number {
  return (u.m2 || 0) + _vagasVinc(lev, u).reduce((s, v) => s + (v.fracao || 0), 0);
}

/**
 * A BASE DE CÁLCULO DE UMA UNIDADE — EL-191, 08/10/2026. É o ÚNICO lugar da regra.
 *
 * Pedido literal (EL-184): "Cálculo do FSF está levando em conta sempre o valor de
 * avaliação. Adicionar na regra que, se o valor de venda for inferior ao valor de venda
 * [de avaliação], este deverá ser o parâmetro." Decisões da calibração de 08/10/2026:
 *
 *   - compara o PACOTE: a unidade + as vagas vinculadas a ela, somadas dos dois lados;
 *   - peça sem valor de venda entra com a própria avaliação no lado da venda — por isso
 *     unidade sem venda fica com a avaliação (e a tabela avisa);
 *   - `u.forcarAvaliacao` (a coluna "Base de cálculo") manda usar a avaliação mesmo assim;
 *   - vale para o FSF, a garantia em estoque e o IGH mês a mês. O VGV NÃO muda (decisão).
 *
 *     base = forçada ? Σ avaliação : mín(Σ venda, Σ avaliação)
 *
 * ⚠ INVARIANTE: `valor ≤ avaliacao`, sempre. A regra só pode BAIXAR o que o motor usava;
 * nunca sobe. Avaliação vazia continua valendo zero, como antes.
 *
 * @param vagas      as vagas vinculadas JÁ RESOLVIDAS e sem as excluídas — quem chama sabe
 *                   de qual tabela elas vêm (`vagasDem` no EL-154, `vagasDet` no EL-147).
 * @param avalSenaoVenda  só o lado EL-147 (`calcIGH`): lá a unidade sem avaliação sempre
 *                   valeu pela venda (`avaliacao || valorVenda`), e isso é preservado.
 */
export function baseDoPacote(u: Unidade, vagas: Vaga[], avalSenaoVenda = false): BaseCalculo {
  const vendaU = u.valorVenda || 0;
  const avalU = avalSenaoVenda ? (u.avaliacao || vendaU) : (u.avaliacao || 0);
  const vendaUEf = vendaU > 0 ? vendaU : avalU;
  let vendaVg = 0, avalVg = 0;
  vagas.forEach(v => {
    const a = v.avaliacao || 0;
    avalVg += a;
    vendaVg += (v.valorVenda || 0) > 0 ? (v.valorVenda as number) : a;
  });
  const venda = vendaUEf + vendaVg, avaliacao = avalU + avalVg;
  const escolha = venda < avaliacao;
  const origem: BaseCalculo['origem'] = u.forcarAvaliacao ? 'forcada' : (escolha ? 'venda' : 'avaliacao');
  const pelaVenda = origem === 'venda';
  return {
    valor: pelaVenda ? venda : avaliacao,
    unidade: pelaVenda ? vendaUEf : avalU,
    vagas: pelaVenda ? vendaVg : avalVg,
    venda, avaliacao, origem,
    semVenda: !(vendaU > 0),
    escolha,
  };
}

/** A base de cálculo da unidade no EL-154 — o pacote com as vagas de `lev.vagasDem`. */
export function baseDem(lev: Lev, u: Unidade): BaseCalculo {
  return baseDoPacote(u, _vagasVinc(lev, u));
}

/**
 * A base de cálculo da unidade no EL-147 — o pacote com as vagas de `lev.vagasDet`, e a
 * unidade sem avaliação valendo pela venda (como o IGH mês a mês sempre leu esse lado).
 * É a que `calcIGH` usa, e a que a coluna "Base de cálculo" do EL-147 mostra.
 *
 * ⚠ NÃO usa `_vagasVinc`, que resolve sempre contra `vagasDem`: os ids de `u.vagas` no
 * lado EL-147 são de `vagasDet`, e cruzar as bases somaria valor de outra tabela.
 */
export function baseDet(lev: Lev, u: Unidade): BaseCalculo {
  return baseDoPacote(u, _vagasVincDet(lev, u), true);
}

/**
 * As vagas de `lev.vagasDet` vinculadas a uma unidade do EL-043 (Detalhamento), sem as
 * excluídas (permutada E informada à Caixa) — o espelho de `_vagasVinc` do lado `det`.
 *
 * Extraída do corpo de `baseDet` no lote 2 de 09/10/2026 (item 16), SEM mudar a conta: a
 * barra do Fluxo passou a somar a FRAÇÃO dessas mesmas vagas, e a resolução do vínculo tem
 * de ser uma só para a base e para a fração. Resolve na ordem dos ids da unidade; id que
 * não existe em `vagasDet` (vínculo órfão) simplesmente não entra.
 */
export function _vagasVincDet(lev: Lev, u: Unidade): Vaga[] {
  const vgs = Array.isArray(lev.vagasDet) ? lev.vagasDet : [];
  const ids = Array.isArray(u.vagas) ? u.vagas : [];
  return ids.map(id => vgs.find(x => x.id === id)).filter((v): v is Vaga => !!v && !_vagaExcl(v));
}

/**
 * A "DATA DE FINANCIAMENTO" QUE VALE para a unidade do EL-147 — 09/10/2026 (lote 2, item 20).
 *
 * Regra de negócio (literal do usuário): "Se a pessoa selecionar a opção “Ingressa para
 * Demanda”, o campo de data de financiamento deve aparecer automaticamente com a data que
 * consta no campo “Cumprimento desejado de demanda”." Decidido na calibração ("Acompanha e
 * vale no IGH"): a data ACOMPANHA o "Cumprimento de demanda" (EL-065) — se ele mudar, muda
 * junto — e VALE no cálculo, não só na tela.
 *
 * Por isso a regra mora aqui, num lugar só, e não numa cópia gravada na linha: a unidade que
 * ingressa (e que é financiada e não permutada — a mesma tríade de `_ingressaValida`) assina
 * no EL-065; as demais, na própria `u.dataRegistro`. A tela (célula travada, exportação, nº de
 * meses do Premissas Mensais), o IGH mês a mês (`ymRegistroUnidade`) e o interruptor do
 * Detalhamento zerado (`vendasDetZeradas`) leem DAQUI — a data que a tela mostra é a que o
 * cálculo usa.
 *
 * Morava em engine/igh.ts até a revisão do lote 2 (10/10/2026); veio para cá porque
 * `vendasDetZeradas` também a lê e demanda.ts não importa de igh.ts (o contrário já acontece).
 * igh.ts a reexporta.
 *
 * '' = sem data: para quem ingressa, o EL-065 vazio; para os demais, a célula vazia.
 */
export function dataFinanciamentoEfetiva(lev: Lev, u: Unidade): IsoDate | '' {
  if (u.ingressaDemanda && u.financiada && !u.permutada) return (lev && lev.dataDemandaDesejada) || '';
  return u.dataRegistro || '';
}

/**
 * O DETALHAMENTO ESTÁ "ZERADO"? — lote 2 de 09/10/2026, item 26. É o ÚNICO lugar da regra.
 *
 * Pedido literal: "Caso os dados de venda em EL-108 estejam zerados, para os cálculos que
 * envolverem o valor de unidade das vendas preenchidas nas linhas vendas fin. e vendas não
 * fin. de EL-070, deve ser pego o VGV do empreendimento e dividido pela quantidade total de
 * unidades. Além disso, o percentual de financiamento deve ser o que está indicado em EL-085."
 *
 * Zerado = NENHUMA unidade não permutada da tabela do Detalhamento (`lev.unidadesDet`) tem
 * "Data de venda" ou "Data de Financiamento" preenchida — a Data de Financiamento que VALE
 * (`dataFinanciamentoEfetiva`): para quem ingressa para a demanda, a do "Cumprimento de demanda",
 * que a célula travada mostra (revisão do lote 2, 10/10/2026 — item 20 × item 26: antes lia só a
 * data gravada, e a unidade que ingressa sem "Data de venda" mostrava uma data que não valia no
 * IGH). Cobre os três estados reais:
 *   - a tabela sem linha nenhuma (quem nunca abriu o Detalhar);
 *   - as linhas em branco que abrir o Detalhar cria (`initUnDet`);
 *   - linhas só com valores, sem data nenhuma (o estado depois de apagar as vendas).
 *
 * Quem usa: `calcIGH` (estoque da barra pela contagem do EL-070 × VGV ÷ U), `calcFluxo`
 * ("Novas Unidades Agregadas" com o % do EL-085) e `_barraDoMes` (com o Detalhamento
 * preenchido, FSF e terreno da barra somam unidade a unidade).
 */
export function vendasDetZeradas(lev: Lev | null | undefined): boolean {
  const det = (lev && Array.isArray(lev.unidadesDet)) ? lev.unidadesDet : [];
  return !det.some(u => !!u && !u.permutada && (!!u.dataVenda || !!dataFinanciamentoEfetiva(lev as Lev, u)));
}

/**
 * AS PREMISSAS MENSAIS NÃO TÊM NENHUMA "VENDAS FIN."? — decisão do usuário de 11/10/2026. É o ÚNICO
 * lugar da regra.
 *
 * Pergunta (caixa de 11/10/2026), literal: "Decisão 2: nos cenários sem "Vendas Fin." nas Premissas
 * Mensais, quantas unidades formam a demanda no Fluxo mensal?" Opção escolhida, literal: "O campo da
 * demanda" — "Volta a usar o campo "Unidades financiadas na demanda" só nesse caso, como antes do lote
 * 4. A tabela e a barra podem divergir quando as Premissas Mensais estão vazias."
 *
 * Verdadeiro quando NENHUM mês de `lev.meses` tem "Vendas Fin." diferente de zero (vazio, ausente ou
 * zero contam como zero) — inclusive sem mês nenhum. NÃO depende do Detalhamento (`vendasDetZeradas`):
 * vale com ele zerado e com ele preenchido. Basta UMA "Vendas Fin." em qualquer mês (mesmo depois do
 * mês da demanda) para deixar de valer.
 *
 * Quem usa: `calcFluxo` — verdadeiro, a demanda do "Fluxo mensal" é o campo "Unidades financiadas na
 * demanda" × a média; falso, a contagem da barra (`_fsfTerrenoDaBarra`).
 */
export function premissasMensaisSemVendasFin(lev: Lev | null | undefined): boolean {
  const meses = (lev && Array.isArray(lev.meses)) ? lev.meses : [];
  return !meses.some(m => !!m && (m.vendFin || 0) !== 0);
}

/**
 * A base de uma vaga avulsa SOLTA (não vinculada): o menor entre venda e avaliação,
 * automático e sem opção de forçar (decisão de 08/10/2026). Sem venda, a avaliação.
 */
export function baseDaVaga(v: Vaga): number {
  const a = v.avaliacao || 0;
  const vd = (v.valorVenda || 0) > 0 ? (v.valorVenda as number) : a;
  return Math.min(vd, a);
}

/**
 * v67 linha 1418 (v57) — GATE central da composição de garantias.
 * Unidade só compõe garantia (FSF + terreno) se ingressa + financiada + NÃO permutada +
 * data de venda ≤ EL-065.
 *
 * ⚠ A comparação de datas é LEXICOGRÁFICA sobre string ISO — só funciona com 'YYYY-MM-DD'.
 */
export function _ingressaValida(lev: Lev, u: Unidade | null | undefined): boolean {
  if (!u || !u.ingressaDemanda || !u.financiada || u.permutada) return false;
  const cut = (lev && lev.dataDemandaDesejada) || '';
  return !!(u.dataVenda && cut && u.dataVenda <= cut);
}

/**
 * A RÉGUA DE SAÍDA DO ESTOQUE na tela de Premissas (EL-093) — 29/07/2026, Parte A do IGH.
 *
 * Regra de negócio (literal do usuário): "tanto vagas quanto unidades só saem do estoque
 * quando o contrato de financiamento que sucede a compra e venda da unidade + vaga é
 * registrado no Registro de Imóveis". No contexto da demanda a data de registro é
 * ASSUMIDA como EL-065 — logo sai exatamente quem a tela já contava como fora:
 *
 *   EL-057 (`demDemanda().validas`) → ingressa + financiada + data de venda ≤ EL-065
 *   EL-114 (`demDerivar().perm`)    → permuta informada ao banco
 *
 * ⚠ É a MESMA régua de antes, escrita como PREDICADO em vez de contagem. Antes o motor
 * fazia `U − EL-057 − EL-114`; agora identifica QUAIS unidades saíram, para poder somar o
 * valor real de quem ficou. A unidade que caísse nos dois conjuntos era descontada DUAS
 * vezes na contagem antiga e agora sai uma só — diferença que só aparece com "permuta
 * informada ao banco" marcada numa unidade que também é válida para demanda.
 *
 * ⚠ NÃO checa `u.permutada` (só `permInfBanco`), porque EL-114 também não checa. Manter o
 * espelho: quem muda um lado tem de mudar o outro.
 */
export function _saiuEstoqueDem(lev: Lev, u: Unidade | null | undefined): boolean {
  if (!u) return false;
  const cut = (lev && lev.dataDemandaDesejada) || '';
  /* EL-057, idêntico a demDemanda(): ingressa + financiada + data de venda ≤ EL-065 */
  const repassada = !!(u.ingressaDemanda && u.financiada && u.dataVenda && cut && u.dataVenda <= cut);
  return repassada || !!u.permInfBanco;   /* EL-114 */
}

/**
 * v67 linha 1416 — SWITCH que decide entre o motor "por unidade" (EL-154) e o motor
 * agregado (campos manuais) em `calcBase`/`calcEstatico`/`terrenoGarFrac`.
 */
export function demTemDados(lev: Lev | null | undefined): boolean {
  const d = (lev && Array.isArray(lev.unidadesDem)) ? lev.unidadesDem : [];
  return d.some(u =>
    (u.avaliacao || 0) > 0 || !!u.dataVenda || !!u.ingressaDemanda ||
    !!u.permComoVenda || !!u.permInfBanco || (Array.isArray(u.vagas) && u.vagas.length > 0));
}

/**
 * v67 linhas 2975-2981 — contagem de unidades válidas para demanda (EL-057) e as duas
 * classes de incoerência (avisos da tela).
 *
 * ⚠ DIVERGÊNCIA REAL, PRESERVADA: aqui NÃO se checa `u.permutada`, mas `_ingressaValida`
 * checa. Logo, uma unidade permutada + financiada + ingressa + data válida CONTA em
 * `validas` (EL-057) e mesmo assim fica FORA do FSF (`fsfPorUnidade`). Corrigir isso é
 * decisão de negócio, não de refactor.
 */
export function demDemanda(L: Lev): ContagemDemanda {
  const d = Array.isArray(L.unidadesDem) ? L.unidadesDem : [];
  const cut = L.dataDemandaDesejada || '';
  let validas = 0, invFin = 0, invDate = 0;
  d.forEach(function (u) {
    if (!u.ingressaDemanda) return;
    if (!u.financiada) { invFin++; return; }                       /* ingressa sem estar financiada */
    if (!u.dataVenda || !cut || u.dataVenda > cut) { invDate++; return; }  /* sem data / sem EL-065 / data > EL-065 */
    validas++;
  });
  return { validas: validas, invFin: invFin, invDate: invDate };
}

/**
 * v67 linhas 2982-2990 — valores derivados da tabela de demanda por unidade.
 *
 * Única mudança em relação ao protótipo: recebe `lev` por PARÂMETRO (lá era
 * `demDerivar()` lendo `estado.rascunho.lev`) e lê `lev.unidadesDem || []` no lugar de
 * `demDet()`, que criava o array por efeito colateral. O corpo do cálculo é idêntico.
 */
export function demDerivar(lev: Lev): DerivadosDemanda {
  /* v67 passa por demDet(), que faz `if(!Array.isArray(L.unidadesDem)) L.unidadesDem=[]`:
     QUALQUER valor não-array vira []. `|| []` só cobria falsy e derrubava com {} ou string. */
  const L = lev, d = Array.isArray(lev.unidadesDem) ? lev.unidadesDem : [];
  /* v59: só a avaliação da unidade (sem vagas, p/ não duplicar as vinculadas) */
  const vgvUn = d.filter(u => !_unitExcl(u)).reduce((s, u) => s + (u.avaliacao || 0), 0);
  /* v59: TODAS as vagas avulsas com avaliação, exceto permutada+informada à Caixa (vinculadas ou não) */
  const vgvVg = (Array.isArray(L.vagasDem) ? L.vagasDem : [])
    .filter(v => !_vagaExcl(v)).reduce((s, v) => s + (v.avaliacao || 0), 0);
  const vgv = Math.round(vgvUn + vgvVg);                          /* EL-083 */
  const cut = L.dataDemandaDesejada || '';
  /* EL-056: Data de venda ≤ EL-065 OU "permuta informada como venda" */
  const vend = d.filter(u => (u.dataVenda && cut && u.dataVenda <= cut) || u.permComoVenda).length;
  const fin = demDemanda(L).validas;                              /* EL-057 */
  const perm = d.filter(u => u.permInfBanco).length;              /* EL-114 */
  return { vgv, vend, fin, perm };
}

/**
 * Parte PURA de `aplicarTravasDemanda` (v67 linhas 2991-2996), sem DOM e sem escrita.
 *
 * Diz QUAIS campos a tabela de demanda assume (trava) e com que valor. No protótipo, os
 * valores travados são GRAVADOS de volta em `b.VGV` / `b.permutadas` / `lev.unidVendDem` /
 * `lev.unidFinDem` e depois lidos pelo motor como se fossem entrada. Aqui a decisão de
 * gravar (ou de projetar sob demanda) fica com a camada de aplicação — o motor não escreve.
 *
 *   pV → EL-083 (VGV)                 recebe `derivados.vgv`
 *   pC → EL-056 (unid. vendidas)      recebe `derivados.vend`
 *   pI → EL-057 (unid. financiadas)   recebe `derivados.fin`
 *   pB → EL-114 (permutadas)          recebe `derivados.perm`
 */
export function demTravas(lev: Lev): TravasDemanda {
  /* v67 passa por demDet(), que faz `if(!Array.isArray(L.unidadesDem)) L.unidadesDem=[]`:
     QUALQUER valor não-array vira []. `|| []` só cobria falsy e derrubava com {} ou string. */
  const L = lev, d = Array.isArray(lev.unidadesDem) ? lev.unidadesDem : [];
  const vagas = Array.isArray(L.vagasDem) ? L.vagasDem : [];
  const pV = d.some(u => (u.avaliacao || 0) > 0) || vagas.some(v => !_vagaExcl(v) && (v.avaliacao || 0) > 0);
  const pC = d.some(u => !!u.dataVenda || !!u.permComoVenda);
  const pI = d.some(u => !!u.ingressaDemanda);
  const pB = demTemDados(L);
  return { pV, pC, pI, pB, derivados: demDerivar(lev) };
}

/**
 * v67 linha 2862 — soma das frações ideais (unidades + vagas) de uma das tabelas.
 *
 * Assinatura trocada: o protótipo recebia a chave de UI (`'det' | 'dem'`) e resolvia os
 * arrays no estado global. Aqui recebe os arrays. Só valida a soma = 1 (aviso de tela);
 * não alimenta cálculo.
 */
export function fracaoTotal(unidades: Unidade[], vagas: Vaga[]): number {
  return unidades.reduce((s, x) => s + (x.m2 || 0), 0) + vagas.reduce((s, v) => s + (v.fracao || 0), 0);
}
